import React, { ReactNode, useEffect } from 'react'
import type { ReactElement } from 'react'
import classNames from 'classnames'
import Image from '@/components/Common/StencilImage'
import { logNewRelicEvent } from '@/root/utils/common/custom/newrelic-page-events'
import { getErrorLogData } from '@/root/utils/common/custom/get-error-log-data'

export default function SomethingWentWrong({
  error,
  children,
  title,
  subTitle,
  showSubTitle = true,
  showReloadCta = true,
  cta,
  isFullScreen = true,
  bumpUpForEye = true,
  onCtaClick = null,
}: {
  error?: Error
  children?: ReactNode
  title?: string
  subTitle?: string
  showSubTitle?: boolean
  showReloadCta?: boolean
  isFullScreen?: boolean
  bumpUpForEye?: boolean
  cta?: {
    text: string
    link?: string
  }
  onCtaClick?: (() => void) | null
}): ReactElement {
  useEffect(() => {
    document.body.classList.remove('bg-brand-grey-background')
    document.body.classList.add('bg-brand-white')

    return () => {
      document.body.classList.add('bg-brand-grey-background')
      document.body.classList.remove('bg-brand-white')
    }
  })

  useEffect(() => {
    if (error) {
      logNewRelicEvent('stencil_error_boundary', {
        error: JSON.stringify(getErrorLogData(error)),
      })
    } else {
      logNewRelicEvent('stencil_error_boundary', {
        error: 'Error is empty',
      })
    }
  }, [error])

  return (
    <div
      className={classNames(
        'flex transform flex-col items-center justify-center font-sans',
        {
          'h-screen w-screen': isFullScreen,
          'md:-translate-y-14': bumpUpForEye,
        },
      )}
    >
      <div className=" w-full md:w-1/3">
        <Image
          src="/common/lost-in-space.png"
          layout="responsive"
          alt="Astronaut lost with a dog in space"
          width={640}
          height={460}
        />
      </div>

      {children ? (
        children
      ) : (
        <div className="px-4 text-center">
          {' '}
          <div
            className={classNames('mt-6 font-medium', {
              'text-3xl': isFullScreen,
              'text-2xl': !isFullScreen,
            })}
          >
            {title ?? `Something went wrong!`}
          </div>
          {showSubTitle && (
            <div className="mt-4 text-xl">
              {subTitle ??
                `Looks like we're having some issues launching this screen`}
            </div>
          )}
          {showReloadCta && (
            <div className="mt-10">
              <button
                className="btn btn-banner"
                onClick={e => {
                  if (cta) {
                    e.preventDefault()
                    if (cta.link) {
                      window.location.href = cta.link
                      return
                    }

                    if (onCtaClick) {
                      onCtaClick()
                    }
                    return
                  }
                  window.location.reload()
                }}
              >
                {cta ? cta.text : 'Try reloading'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
